import React, { useCallback } from "react";
import Button from "../Button/Button";
import styles from "./ButtonPair.module.css";

/**
 * @typedef {Object} ButtonPairProps
 * @property {string} [selected="and"] - The value of the currently active/selected button.
 * @property {(value: string) => void} [onClick] - Callback fired when a button is selected, returning its associated value.
 * @property {boolean} [disabled=false] - If true, disables interaction for both buttons.
 * @property {boolean} [inGroup=false] - Controls layout margins; true uses inner-group padding, false uses top-level margins.
 * @property {string} [leftLabel="And"] - Text label displayed inside the left button.
 * @property {string} [rightLabel="Or"] - Text label displayed inside the right button.
 * @property {string} [leftValue="and"] - Value returned by onClick when clicking the left button.
 * @property {string} [rightValue="or"] - Value returned by onClick when clicking the right button.
 * @property {string} [className=""] - External CSS class name to apply to the container root element.
 * @property {React.CSSProperties} [style={}] - Inline CSS styles to apply to the container root element.
 * @property {{ root?: string, button?: string, selected?: string }} [classes={}] - Styling class overrides for inner sub-elements.
 */

/**
 * ButtonPair component displays a pair of toggle buttons (e.g. And/Or).
 * It is framework-independent and uses CSS Modules.
 * 
 * @param {ButtonPairProps} props
 */
const ButtonPair = ({
  selected = "and",
  onClick,
  disabled = false,
  inGroup = false,
  leftLabel = "And",
  rightLabel = "Or",
  leftValue = "and",
  rightValue = "or",
  className = "",
  style = {},
  classes = {},
}) => {
  const triggerClick = useCallback(
    (value, event) => {
      if (disabled) return;

      // Prevent double invocation for mouse clicks (which trigger both mouseup and click)
      if (event.type === "click" && event.detail > 0) {
        return;
      }

      onClick?.(value);
    },
    [disabled, onClick]
  );

  const handleLeftEvent = useCallback(
    (e) => {
      triggerClick(leftValue, e);
    },
    [leftValue, triggerClick]
  );

  const handleRightEvent = useCallback(
    (e) => {
      triggerClick(rightValue, e);
    },
    [rightValue, triggerClick]
  );

  const containerClass = [
    styles.buttonPair,
    inGroup ? styles.inGroup : styles.notInGroup,
    classes.root,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClass} style={style}>
      <Button
        disabled={disabled}
        onClick={handleLeftEvent}
        onMouseUp={handleLeftEvent}
        className={[
          styles.toggleButton,
          classes.button,
          selected === leftValue ? `${styles.selected} ${classes.selected || ""}` : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {leftLabel}
      </Button>
      <Button
        disabled={disabled}
        onClick={handleRightEvent}
        onMouseUp={handleRightEvent}
        className={[
          styles.toggleButton,
          classes.button,
          selected === rightValue ? `${styles.selected} ${classes.selected || ""}` : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {rightLabel}
      </Button>
    </div>
  );
};

export default ButtonPair;
